import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import { Suspense, lazy, useCallback, useMemo } from "react";
import { processRead, readField, writeField } from "../utils/objectUtilities";
import { Spinner } from "react-bootstrap";
import { materializeValue } from "../utils/lazyValue";
import { safeEval } from "../utils/safeEval";
import { ensureArray } from "../utils/arrayUtilities";
import { materialize } from "../utils/materializationUtilities";

const processWrite = (value, field) => {
    if (!field) {
        return undefined;
    } else if (typeof field === 'string') {
        const delta = {};
        writeField(delta, field, value);
        return delta;
    } else if (Array.isArray(field)) {
        return field.reduce((obj, f) => {
            if (typeof f === 'string') {
                writeField(value, f, readField(obj, f));
            } else {
                writeField(value, f.src, readField(obj, f.dest));
            }
            return value;
        }, {});
    } else {
        return Object.entries(field).reduce((obj, [ dest, src ]) => {
            writeField(obj, src, readField(value, dest));
            return obj;
        }, {});
    }
}

const createCtrlRegistry = () => {
    const registry = { };

    const register = (kind, load, { defaultPrintValue = () => ' ', supportsEditor = true, ...props } = {}) => {
        if (registry[kind]) {
            console.warn(`Component type "${kind}" is already registered.`);
            return;
        }
        if (!!module.queryControl !== !!module.comparator) {
            throw new Error("If you implement a comparator or define a queryControl you need to ensure you implement both!");
        }
        const buildComponent = fields => lazy(async () => {
            const module = await load();
            const registration = module.customCtrlDef;
            if (!registration) {
                throw new Error("the module loaded does not have a customCtrlDef export - bad module - " + kind);
            }
            const field = ensureArray(fields).find(field => registration[field]);
            if (!field) {
                throw new Error("no controls were found in the fields specified for kind " + kind);
            }
            const control = registration[field];
            if (typeof control !== 'function') {
                throw new Error("control is not a valid React node rednering kind " + kind);
            }
            return { default: control };
        });
        
        const Component = buildComponent('control');
        const QueryComponent = buildComponent(['queryControl', 'control']);

        registry[kind] = { kind, Component, QueryComponent, load, defaultPrintValue, supportsEditor, ...props };
    }

    const editorComponents = (t, filter = entry => entry.supportsEditor) => {
        const components = Object.values(registry).reduce((res, entry) => {
            if (filter(entry)) {
                res.push({ kind: entry.kind, label: t?.(entry.kind), defaults: entry.defaults });
            }
            return res;
        }, []);
        components.sort((a, b) => {
            if (a.label < b.label) {
                return -1;
            } else if (a.label > b.label) {
                return 1;
            }
            return 0;
        })
        return components;
    };

    const editorDef = async (t, kind, tools) => {
        const base = await registry[kind]?.load()
        if (!base?.customCtrlDef) {
            throw new Error("customCtrlDef export from kind " + kind + " not found in custom control editorDef");
        }
        const module = base.customCtrlDef;
        return module.def(t, tools);
    }

    const render = (kind, props, context, { componentField = 'Component' } = {}) => {
        if (!registry[kind]) {
            console.warn(`There is no registered custom component of type "${kind}".`);
            return;
        }
        const Component = registry[kind][componentField];
        return <Suspense fallback={<Spinner></Spinner>}>
            <Component context={context} {...props}></Component>
        </Suspense>
    }

    const toString = async (kind, props) => {
        if (!registry[kind]) {
            throw new Error("Unknown control kind " + kind + " in custom control toString")
        }
        const base = await registry[kind].load()
        if (!base?.customCtrlDef) {
            throw new Error("customCtrlDef export from kind " + kind + " not found in custom control toString");
        }
        const module = base.customCtrlDef;
        if (Object.hasOwn(module, 'toString')) {
            return await module?.toString(props);
        }
        return (await materializeValue(props?.value))?.toString?.() ?? props?.defaultValue;
    }

    const fromString = async (kind, text, props) => {
        if (!registry[kind]) {
            throw new Error("Unknown control kind " + kind + " in custom control toString")
        }
        const base = await registry[kind].load()
        if (!base?.customCtrlDef) {
            throw new Error("customCtrlDef export from kind " + kind + " not found in custom control toString");
        }
        const module = base.customCtrlDef;
        if (Object.hasOwn(module, 'fromString')) {
            return await module?.fromString(props);
        }
        return text ?? undefined;
    }

    const persist = async (kind, props) => {
        if (!registry[kind]) {
            throw new Error("Unknown control kind " + kind + " in custom control persist")
        }
        if (!props?.def?.persist === true || !props?.def?.field) {
            return undefined;
        }
        const base = await registry[kind].load()
        if (!base?.customCtrlDef) {
            throw new Error("customCtrlDef export from kind " + kind + " not found in custom control persist");
        }
        const module = base.customCtrlDef;
        let value = props?.value;
        if (Object.hasOwn(module, 'persist')) {
            value = await module?.persist(props);
        }
        return processWrite(value, props?.def?.field);
    }

    const defaultPrintValue = (kind) => {
        return registry[kind].defaultPrintValue();
    }

    const clear = () => {
        for (const kind of Object.getOwnPropertyNames(registry)) {
            delete registry[kind];
        }
    }

    const compare = async (def, query, value) => {
        const kind = def.type;
        if (!registry[kind]) {
            throw new Error("Unknown control kind " + kind + " in custom control compare")
        }
        const base = await registry[kind].load()
        if (!base?.customCtrlDef) {
            throw new Error("customCtrlDef export from kind " + kind + " not found in custom control compare");
        }
        const module = base.customCtrlDef;
        if (Object.hasOwn(module, 'comparator')) {
            return await module?.comparator(query, value, { def, compare });
        } else if (query?.value === undefined) {
            return true;
        } else if (Array.isArray(query?.value)) {
            return query?.value?.every(v => ensureArray(value?.value).some(v2 => v2 == v));
        } else if (typeof query?.value === 'object') {
            return Object.keys(query?.value).every(qf => query?.value?.[qf] == value?.value?.[qf]);
        }
        return query?.value == value?.value;
    }

    return { register, render, toString, fromString, persist, defaultPrintValue, editorComponents, editorDef, clear, compare };
}

export const ctrlRegistry = createCtrlRegistry();

export const CustomCtrl = ({ def, value: obj, args, disabled = false, readonly = false, restricted = false, required = false, hideLabel = false, live = false, onChange, mode = 'default', ...props }) => {
    const value = useMemo(() => processRead(obj, def.field), [obj, def.field]);
    const clientConfig = useClientConfig();
    const { t, i18n } = useTranslation();
    
    const isDisabled = disabled || def.readonly || (restricted && def.restricted);
    const isReadonly = readonly || def.readonly;
    const isLive = live || def.live;
    const displayLabel = def.label ? def.label : '';

    const handleChange = useCallback((value, field) => {
        
        if (field && def.field !== field && def.field) {
            if (Array.isArray(def.field)) {
                field = def.field.find(f => f.dest === field)?.src ?? field;
            } else if (typeof def.field === 'object') {
                field = def.field[field] ?? field;
            }
        } else if (!field && def.field) {
            if (Array.isArray(def.field)) {
                field = def.field.find(f => f.dest === 'value')?.src ?? field;
            } else if (typeof def.field === 'object') {
                field = def.field.value ?? field;
            }
        }
        
        onChange(value, field);
    }, [def.field, onChange])

    const href = useMemo(() => {
        if (typeof def.href === 'string') {
            return def.href;
        } else if (typeof def.href === 'function') {
            return def.href(obj);
        }
    }, [obj, def])

    const context = useMemo(() => {
        return { clientConfig, t, language: i18n.resolvedLanguage };
    }, [clientConfig, t, i18n.resolvedLanguage])
    
    const argsToChain = useMemo(() => {
        return typeof args === 'function' ? args(def, obj) : args;
    }, [args, def, obj])
    const extendedProps = useMemo(() => ({ def, ...value, disabled: isDisabled, readonly: isReadonly, restricted, required, live: isLive, placeholder: displayLabel, hideLabel, onChange: handleChange, href, args: argsToChain, ...props }), [value, isDisabled, isReadonly, isLive, displayLabel, hideLabel, handleChange, props, restricted, required, def, href, argsToChain]);

    const componentField = useMemo(() => {
        let componentField;
        switch(mode) {
            case 'default':
                componentField = "Component";
                break;
            case 'query':
                componentField = 'QueryComponent';
                break;
            default:
                throw new Error("Unknown CustomCtrl mode encountered - expected default or query got " + mode);
        }
        return componentField;
    }, [mode]);

    return ctrlRegistry.render(def.type, extendedProps, context, { componentField });
}

export const customFieldToString = async (def, obj, realizationContext, defaultValue = '', language = 'en', args = undefined) => {
    return ctrlRegistry.toString(def.type, { def, ...processRead(obj, def.field), defaultValue, ...realizationContext, language, args });
}

export const customFieldFromString = async (def, text, realizationContext, language = 'en', args = undefined) => {
    return ctrlRegistry.fromString(def.type, text, { def, ...realizationContext, language, args });
}

export const customFormToStringObject = async (formDef, input, realizationContext, language = 'en') => {
    const cellToString = async (entry) => {
        const data = entry?.data;
        const result = {};
        if (!Array.isArray(formDef)) {
            return result;
        }
        for (const section of formDef) {
            if (section?.cond) {
                const include = safeEval(`(section, data) => ${section.cond}`)(section, data);
                if (!include) continue;
            }
            const rows = section?.rows;
            for (const row of rows) {
                for (const cell of (row)) {
                    if (!cell) continue;
                    if (cell.cond) {
                        const include = safeEval(`(def, data, args) => ${cell.cond}`)(cell, data, null);
                        if (!include) continue;
                    }
                    if (!cell.field) continue;
                    switch (cell.type) {
                        case 'table': {
                            const value = processRead(data, cell.field);
                            const tableValue = Array.isArray(value) ? value : readField(data, cell.field);
                            if (Array.isArray(tableValue)) {
                                const rows = [];
                                for (const tableRow of tableValue) {
                                    const rowObj = {};
                                    for (const col of cell.columns) {
                                        if (!col?.field) continue;
                                        rowObj[col.field] = await customFieldToString(col, tableRow);
                                    }
                                    rows.push(rowObj);
                                }
                                result[cell.field] = rows;
                            }
                            break;
                        }
                        case 'image':
                        case 'signature': {
                            const obj = readField(data, cell.field);
                            if (typeof obj === 'string' && obj.startsWith('data:')) {
                                result[cell.field] = obj;
                            } else {
                                result[cell.field] = await materialize(realizationContext, obj);
                            }
                            break;
                        }
                        default: {
                            result[cell.field] = await customFieldToString(cell, data, realizationContext, '', language);
                            break;
                        }
                    }
                }
            }
        }
        return result;
    };
    if (Array.isArray(input)) {
        return Promise.all(input.map(e => cellToString(e)));
    }
    return cellToString(input);
}

const prefixField = (prefix, field) => {
    return prefix ? `${prefix}.${field}` : field;
}

export const customFormQuery = async (formDef, array, query, { prefix = '' } = {}) => {
    const toCheck = prefix ? readField(query, prefix) : query;
    if (!toCheck || (typeof toCheck === 'object' && Object.keys(toCheck).length === 0)) {
        return array;
    }
    
    const defs = formDef.map(s => s.rows).flat(2);
    async function matchQuery(item, query) {
        for (const cell of defs) {
            const queryProps = processRead(query, prefixField(prefix, cell.field));
            const valueProps = processRead(item, prefixField(prefix, cell.field));
            if (!await ctrlRegistry.compare(cell, queryProps, valueProps)) {
                return false;
            }
        }
        return true;
    }

    let result = [];
    for (const item of array) {
        if (await matchQuery(item, query)) {
            result.push(item);
        }
    }
    return result;
}

export const customFieldPersist = async (def, obj) => {
    return await ctrlRegistry.persist(def.type, { def, ...processRead(obj, def.field) });
}

export const defaultPrintValue = def => ctrlRegistry.defaultPrintValue(def.type);

export const editorComponents = ctrlRegistry.editorComponents;

export const editorDef = ctrlRegistry.editorDef;

export default CustomCtrl;